import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { Orcamento } from '../models/workflow.models';
import { buildSeedData } from './seed-data';
import { WorkflowsService } from './workflows.service';

@Injectable({ providedIn: 'root' })
export class OrcamentosService {
  private storageKey = 'wf_orcamentos';
  private orcamentosSubject = new BehaviorSubject<Orcamento[]>([]);

  constructor(private workflowsService: WorkflowsService) {
    const stored = this.loadFromStorage();
    if (stored.length) {
      this.orcamentosSubject.next(stored);
    } else {
      const seed = buildSeedData();
      this.orcamentosSubject.next(seed.orcamentos);
      this.persist(seed.orcamentos);
    }
  }

  getAll(): Observable<Orcamento[]> {
    return this.orcamentosSubject.asObservable();
  }

  getSnapshot(): Orcamento[] {
    return this.orcamentosSubject.value;
  }

  getById(id: number): Observable<Orcamento | undefined> {
    return this.orcamentosSubject.asObservable().pipe(
      map((items) => items.find((item) => item.id === id))
    );
  }

  addOrcamento(orcamento: Orcamento): void {
    const current = this.orcamentosSubject.value;
    const nextId = current.length ? Math.max(...current.map((item) => item.id)) + 1 : 1;
    const novo = { ...orcamento, id: nextId };
    const updated = [...current, novo];
    this.orcamentosSubject.next(updated);
    this.persist(updated);
  }

  setAll(items: Orcamento[]): void {
    this.orcamentosSubject.next(items);
    this.persist(items);
  }

  promoverParaLead(id: number): void {
    this.updateStatus(id, 'LEAD');
  }

  converterParaOS(id: number): void {
    const orcamento = this.orcamentosSubject.value.find((item) => item.id === id);
    if (!orcamento) {
      return;
    }

    if (orcamento.status !== 'OS') {
      this.updateStatus(id, 'OS');
      this.workflowsService.addWorkflowFromOrcamento({ ...orcamento, status: 'OS' });
    }
  }

  private updateStatus(id: number, status: Orcamento['status']): void {
    const updated = this.orcamentosSubject.value.map((item) =>
      item.id === id ? { ...item, status } : item
    );
    this.orcamentosSubject.next(updated);
    this.persist(updated);
  }

  private loadFromStorage(): Orcamento[] {
    if (typeof localStorage === 'undefined') {
      return [];
    }
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as Orcamento[];
    return parsed.map((item) => ({
      ...item,
      criadoEm: new Date(item.criadoEm),
      dataCriacao: item.dataCriacao ? new Date(item.dataCriacao) : undefined,
      dataFechamento: item.dataFechamento ? new Date(item.dataFechamento) : null,
      ultimoContatoEm:
        item.ultimoContatoEm === undefined
          ? undefined
          : item.ultimoContatoEm
            ? new Date(item.ultimoContatoEm)
            : null,
      proximoContatoEm:
        item.proximoContatoEm === undefined
          ? undefined
          : item.proximoContatoEm
            ? new Date(item.proximoContatoEm)
            : null,
      dataPrevistaEntrega:
        item.dataPrevistaEntrega === undefined
          ? undefined
          : item.dataPrevistaEntrega
            ? new Date(item.dataPrevistaEntrega)
            : null,
    }));
  }

  private persist(items: Orcamento[]): void {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem(this.storageKey, JSON.stringify(items));
  }
}
